import { createStore, applyMiddleware, compose } from "redux";
import thunk from "redux-thunk";
import rootReducer from "../reducer";
import { updatePersistedJobState } from "../../services/ws/JobPersistenceService";
import { setSessionTenant } from "../../global/sessionScope";
import { getActiveTenantName } from "../../global/tenant";
import { installPerfProfiler } from "../../utils/ganttPerfProfiler";
import { cancelFollowsOnScenarioSwitch } from "../../services/schedule-activity-services/scheduleFollowRegistry";

const initalState = {};
const middleware = [thunk];

// Diagnostic only, and only when the operator asked for it through
// localStorage["gantt-perf-debug"]. Sits BEFORE thunk so a thunk's own
// synchronous body is timed, not just the plain actions it dispatches.
const perfProfiler = installPerfProfiler();
if (perfProfiler) {
  middleware.unshift(perfProfiler);
}

let store;

const ReactReduxDevTools =
  window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__();

if (window.navigator.userAgent.includes("Chrome") && ReactReduxDevTools) {
  store = createStore(
    rootReducer,
    initalState,
    compose(applyMiddleware(...middleware), ReactReduxDevTools)
  );
} else {
  store = createStore(
    rootReducer,
    initalState,
    compose(applyMiddleware(...middleware))
  );
}

// ── WS job state persistence ──────────────────────────────────────────────
// After each Redux dispatch, snapshot active job progress to localStorage so
// totalProjects, completedCount and etaSeconds survive a page refresh.
// We debounce writes to avoid hammering localStorage on rapid frame sequences.
// Every slice whose jobs can be REHYDRATED after a refresh (see
// KIND_STARTED_TYPES in useJobRehydration) must persist its progress here —
// rehydration re-dispatches STARTED with these counts, and the terminal poll
// carries only a status. A missing slice therefore restores as 0/0/0, which
// makes a partially-failed job resolve as a clean success.
const WS_SLICE_KEYS = [
  "import", "delete", "updateUsages", "scenarioCopy",
  "mppBatchImport", "aiImport", "scenarioDelete", "autoFeedActuals",
  "resetCapacity", "generativeScheduling",
  "structureCsvImport",
];

let _persistTimer = null;
store.subscribe(() => {
  if (_persistTimer) return; // already scheduled
  _persistTimer = setTimeout(() => {
    _persistTimer = null;
    const state = store.getState();
    WS_SLICE_KEYS.forEach((key) => {
      const slice = state[key];
      if (!slice?.jobs) return;
      Object.values(slice.jobs).forEach((job) => {
        if (!job.active || !job.jobId) return;
        updatePersistedJobState(job.jobId, {
          totalProjects:  job.totalProjects  ?? 0,
          completedCount: job.completedCount ?? 0,
          failedCount:    job.failedCount    ?? 0,
          etaSeconds:     job.etaSeconds     ?? null,
          fileName:       job.fileName       ?? null,
        });
      });
    });
  }, 300); // 300 ms debounce — coalesces burst frames into one write
});

// Push the active tenant into the leaf session-scope so low-level utilities
// (e.g. JobPersistenceService) can read it WITHOUT importing the store — which
// would form an import cycle (store already imports JobPersistenceService above).
// Guarded so this per-action subscriber only writes when the tenant changes.
let _lastTenant;
store.subscribe(() => {
  const tenant = getActiveTenantName(store.getState());
  if (tenant !== _lastTenant) {
    _lastTenant = tenant;
    setSessionTenant(tenant);
  }
});

// A schedule follow of the scenario left behind must not toast, refetch or show its card on the
// next one, whichever route opened it.
cancelFollowsOnScenarioSwitch(store);

export default store;
